import { Component, OnInit } from '@angular/core';
import { OperationError } from '../../../../../models/server-result/operation-error';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { ServerApiService } from '../../../../../server-api/server-api.service';
import { LogService } from '../../../../../services/log/log.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { ValidateFileContentByUploadIdRequest } from '../../../../../models/upload/validate-file-content-by-upload-id-request';
import { ValidateFileContentByUploadIdResult } from '../../../../../models/upload/validate-file-content-by-upload-id-result';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { SupplierRecordValidation } from '../../../../../models/upload/suppliers/supplier-record-validation-result';
import { ProcessFileContentByUploadIdRequest } from '../../../../../models/upload/process-file-content-by-upload-id-request';
import { ConfirmingContract } from '../../../../../models/contracts/confirming/confirming-contract';
import { PublicContract } from '../../../../../models/contracts/confirming/public-contract';
import { ScreenWidthListenerService } from '../../../../../services/screen-width-detector/screen-width-listener.service';
import { SepaSearchCriteria } from '../../../../../models/sepa/sepa-search-criteria';
import { Sepa } from '../../../../../models/sepa/sepa';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { UploadSuppliersInstructionsModalComponent } from '../instructions-modal/upload-suppliers-instructions-modal.component';
import { ContractAndRelatedConnectionHelperService } from '../../../../../services/contract-related-connection/contract-related-connection-helper.service';
import { AppContextService } from '../../../../../services/app-context.service';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { User } from '../../../../../models/User/user';
import { CustomModalService } from '../../../../../shared-components/custom-modal/custom-modal.service';

@Component({
	selector: 'app-suppliers-upload-step3',
	templateUrl: './suppliers-upload-step3.component.html',
	styleUrls: ['../upload-suppliers.scss'],
	standalone: false,
})
export class SuppliersUploadStep3Component implements OnInit {
	public isPerformingRequest = false;
	public validationErrors: OperationError[] = [];
	public loggedUser: User | null = null;
	public contentValidationData: SupplierRecordValidation[] = [];
	public uploadId: string;
	public associatedCompanyId: string;
	private returnContractId: string = '';
	public totalValidRecords: number = 0;
	public totalInvalidRecords: number = 0;
	public showColumnActiveAutomaticFundingRequest: boolean = false;
	public showValidationResults: boolean;
	protected loadedRecordsCounts: Promise<boolean>;

	currentStep: number = 2;
	steps: Step[] = [];

	constructor(
		private translateService: TranslateService,
		private serverApiService: ServerApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private router: Router,
		private route: ActivatedRoute,
		private modalService: CustomModalService,
		protected screenWidthListener: ScreenWidthListenerService,
		private contractAndRelatedConnectionHelperService: ContractAndRelatedConnectionHelperService,
		private appContext: AppContextService,
		private routeAuthorizationService: RouteAuthorizationService
	) {
		this.initializeSteps();
	}

	initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		const queryParams = this.route.snapshot.queryParams;
		if (queryParams['returnContractId']) {
			this.returnContractId = queryParams['returnContractId'];
		}

		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (
				(this.loggedUser?.IsBuyer && this.loggedUser?.IsCompanyAdmin) ||
				(this.loggedUser?.IsSystemAdmin && !this.loggedUser.IsApiIntegrationUser) ||
				this.loggedUser?.IsSupportOperator
			) {
				this.route.params.subscribe((params) => {
					this.uploadId = params['uploadId'];
					this.associatedCompanyId = params['associatedCompanyId'];

					if (this.uploadId && this.associatedCompanyId) {
						const queryParams = this.route.snapshot.queryParams;
						const contractId = queryParams['contractId'] ? queryParams['contractId'] : null;

						const validateFileContentByUploadIdRequest: ValidateFileContentByUploadIdRequest = <ValidateFileContentByUploadIdRequest>{
							UploadId: this.uploadId,
							CompanyId: this.associatedCompanyId,
							ContractId: contractId,
						};

						const serviceCall = this.serverApiService.validateSupplierRecordsExcelFileContent(validateFileContentByUploadIdRequest);
						this.isPerformingRequest = true;
						serviceCall.subscribe({
							next: (operationResult: OperationResult<ValidateFileContentByUploadIdResult<SupplierRecordValidation>>) => {
								this.isPerformingRequest = false;
								if (operationResult.IsValid) {
									this.processValidValidateFileContentByUploadIdResult(operationResult);
								} else {
									this.processInValidValidateFileContentByUploadIdResult(operationResult);
								}
							},
							error: (httpErrorResponse: HttpErrorResponse) => {
								this.isPerformingRequest = false;
								this.logger.error(
									'[SuppliersUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ',
									httpErrorResponse
								);

								this.toasterService.showError(
									'[SuppliersUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ' +
										httpErrorResponse.error +
										httpErrorResponse.message
								);
							},
						});
					} else {
						this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_UPLOAD_ID_FROM_ROUTE', 'list-errorCodes'));
					}
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	private processValidValidateFileContentByUploadIdResult(
		operationResult: OperationResult<ValidateFileContentByUploadIdResult<SupplierRecordValidation>>
	): void {
		this.showValidationResults = true;
		this.contentValidationData = operationResult.Data.ContentValidationData;

		this.totalValidRecords = operationResult.Data.TotalValidRecords;
		this.totalInvalidRecords = operationResult.Data.TotalInvalidRecords;

		this.loadedRecordsCounts = Promise.resolve(true);

		const contractsIds: string[] = [];

		operationResult.Data.ContentValidationData.forEach((contentData: SupplierRecordValidation) => {
			if (!!contentData.ConfirmingContractId && !contractsIds.find((c) => c == contentData.ConfirmingContractId))
				contractsIds.push(contentData.ConfirmingContractId);
		});

		if (!!contractsIds && contractsIds.length > 0) {
			const getContractsByIdsServiceCall = this.serverApiService.getConfirmingContractsByIds(contractsIds);

			getContractsByIdsServiceCall.subscribe({
				next: (operationResult: OperationResult<ConfirmingContract[]>) => {
					if (operationResult.IsValid) {
						this.contentValidationData.forEach((contentData: SupplierRecordValidation) => {
							contentData.ConfirmingContract = operationResult.Data.find((c) => c.Id === contentData.ConfirmingContractId) || null;
						});

						this.isAutomaticFundingRequestColumnVisible(this.contentValidationData);

						this.contentValidationData = [...this.contentValidationData];
					} else {
						console.warn('Error retrieving contract information using getConfirmingContractsByIds... IsValid=false');
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				},
			});
		}

		if (operationResult.Data.ContentValidationData?.some((contentData: SupplierRecordValidation) => !!contentData.BankCountryCode)) {
			const sepaSearchCriteria: SepaSearchCriteria = new SepaSearchCriteria();
			const getSepaConfigurations = this.serverApiService.getSepaConfigurations(sepaSearchCriteria);

			getSepaConfigurations.subscribe({
				next: (operationResult: OperationResult<Sepa[]>) => {
					if (operationResult.IsValid) {
						this.contentValidationData.forEach((contentData: SupplierRecordValidation) => {
							if (!!contentData.BankCountryCode) {
								contentData.BankCountry =
									operationResult.Data.find((c) => c.CountryCode === contentData.BankCountryCode)?.CountryName || '';
							}
						});

						this.contentValidationData = [...this.contentValidationData];
					} else {
						console.warn('Error retrieving SEPA configuration information using getSepaConfigurations... IsValid=false');
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				},
			});
		}
	}

	private processInValidValidateFileContentByUploadIdResult(
		operationResult: OperationResult<ValidateFileContentByUploadIdResult<SupplierRecordValidation>>
	): void {
		let errors = operationResult.Errors;

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}

		this.showValidationResults = true;
		this.totalInvalidRecords = 1;
		this.loadedRecordsCounts = Promise.resolve(true);
	}

	public backToUploadStep(): void {
		const queryParams: any = {};
		if (this.returnContractId) {
			queryParams.returnContractId = this.returnContractId;
		}

		const currentQueryParams = this.route.snapshot.queryParams;
		if (currentQueryParams['buyerId']) {
			queryParams.buyerId = currentQueryParams['buyerId'];
		}
		this.router.navigate(['/private/files-upload/upload/suppliers/step2', this.associatedCompanyId], {
			queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
		});
	}

	public goToResumeStep(): void {
		const queryParams: any = {};
		if (this.returnContractId) {
			queryParams.returnContractId = this.returnContractId;
		}

		const currentQueryParams = this.route.snapshot.queryParams;
		if (currentQueryParams['buyerId']) {
			queryParams.buyerId = currentQueryParams['buyerId'];
		}
		this.router.navigate(['/private/files-upload/upload/suppliers/step4', this.associatedCompanyId, this.uploadId], {
			queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
		});
	}

	public submitRecords() {
		const queryParams: any = {};
		if (this.returnContractId) {
			queryParams.returnContractId = this.returnContractId;
		}

		const currentQueryParams = this.route.snapshot.queryParams;
		if (currentQueryParams['buyerId']) {
			queryParams.buyerId = currentQueryParams['buyerId'];
		}
		if (this.totalValidRecords === 0)
			this.router.navigate(['/private/files-upload/upload/suppliers/step4', this.associatedCompanyId, this.uploadId], {
				queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
			});
		else {
			const processFileContentByUploadIdRequest: ProcessFileContentByUploadIdRequest = <ProcessFileContentByUploadIdRequest>{
				UploadId: this.uploadId,
			};

			const modalRef = this.modalService.confirm({
				size: 'lg',
				title: this.translateService.translate('supplier-upload-step3.modalConfirmDialogConfirmSubmissionTitle', 'client'),
				body: {
					content: this.translateService
						.translate('supplier-upload-step3.modalConfirmDialogConfirmSubmissionContent', 'client')
						.replace('[numberOfValidSuppliers]', this.totalValidRecords.toString())
						.replace('[totalNumberOfSuppliers]', (this.totalValidRecords + this.totalInvalidRecords).toString()),
				},
			});

			modalRef.result
				.then((confirmed) => {
					if (!confirmed) {
						return;
					}

					const serviceCall = this.serverApiService.processSupplierRecordsExcelFileContentByUploadId(processFileContentByUploadIdRequest);
					this.isPerformingRequest = true;
					serviceCall.subscribe({
						next: (operationResult: OperationResult<ValidateFileContentByUploadIdResult<SupplierRecordValidation>>) => {
							this.isPerformingRequest = false;
							if (operationResult.IsValid) {
								this.processValidProcessFileContentByUploadIdResult();
							} else {
								this.processInvalidProcessFileContentByUploadIdResult(operationResult);
							}
						},
						error: (httpErrorResponse: HttpErrorResponse) => {
							this.isPerformingRequest = false;
							this.logger.error(
								'[SuppliersUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ',
								httpErrorResponse
							);

							this.toasterService.showError(
								'[SuppliersUploadStep3Component::ValidateFileContentByUploadIdRequest] HttpErrorResponse: ' +
									httpErrorResponse.error +
									httpErrorResponse.message
							);
						},
					});
				})
				.catch(() => undefined);
		}
	}

	public processValidProcessFileContentByUploadIdResult(): void {
		const queryParams: any = {};
		if (this.returnContractId) {
			queryParams.returnContractId = this.returnContractId;
		}

		const currentQueryParams = this.route.snapshot.queryParams;
		if (currentQueryParams['buyerId']) {
			queryParams.buyerId = currentQueryParams['buyerId'];
		}
		this.router.navigate(['/private/files-upload/upload/suppliers/step4', this.associatedCompanyId, this.uploadId], {
			queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
		});
	}

	public processInvalidProcessFileContentByUploadIdResult(
		operationResult: OperationResult<ValidateFileContentByUploadIdResult<SupplierRecordValidation>>
	): void {
		let errors = operationResult.Errors;

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	private isAutomaticFundingRequestColumnVisible(contentValidationData: SupplierRecordValidation[]): void {
		this.showColumnActiveAutomaticFundingRequest = contentValidationData?.some((t) =>
			this.contractAndRelatedConnectionHelperService.canActivateAutomaticFundindRequest(t.ConfirmingContract as PublicContract)
		);
	}

	protected openInstructionsModal(): void {
		const instructionsModalRef = this.modalService.open<string>({
			size: 'lg',
			title: this.translateService.translate('supplier-upload-instructions-modal.title', 'client'),
			body: {
				kind: 'component',
				componentType: UploadSuppliersInstructionsModalComponent,
			},
			actions: [
				{
					id: 'start',
					label: this.translateService.translate('button.start', 'client') || 'Start',
					color: 'primary',
					variant: 'outlined',
					autoFocus: true,
					run: ({ bodyComponent, modalRef }) => {
						const result = bodyComponent?.start();
						modalRef.close(result);
					},
				},
			],
		});

		instructionsModalRef.result.catch(() => undefined);
	}
}
